import React, { useEffect } from "react";
import { notification, Spin, Button, Typography } from "antd";
import { useMutation } from "react-query";
import { verifyEmail } from "../../../service/authService";
import { AuthLayout } from "../auth-layout/AuthLayout";
import styled from "styled-components";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Title: AntTitle, Text } = Typography;

interface EmailVerificationProps {
  token: string;
  refetch: () => void;
}

// Global flag to prevent double verification in StrictMode
let globalVerificationAttempted = false;

const VerificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 40px 20px;
  text-align: center;
  color: #FEFFFC;
`;

const IconWrapper = styled.div<{ success: boolean }>`
  font-size: 80px;
  color: ${(props) => (props.success ? "#EBFED1" : "#ff4d4f")};
  margin-bottom: 8px;
`;

const StyledTitle = styled(AntTitle)`
  &.ant-typography {
    color: #FEFFFC !important;
    margin: 0 !important;
    font-size: 32px !important;
    font-weight: 600;
  }
`;

const Message = styled(Text)`
  font-size: 18px;
  color: #FEFFFC;
  opacity: 0.9;
  line-height: 1.6;
  max-width: 400px;
`;

const StyledButton = styled(Button)``;

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  token,
  refetch,
}) => {
  const { t } = useTranslation();
  const verificationAttempted = React.useRef(false);

  const { mutate, isLoading, isSuccess, isError, error } = useMutation(
    () => verifyEmail(token),
    {
      onSuccess: () => {
        notification.success({
          message: t("emailVerification.successNotificationMessage"),
          description: t("emailVerification.successNotificationDescription"),
          placement: "bottomRight",
        });

        // Wait a moment to show success state, then reload to log in
        setTimeout(() => {
          window.location.href = "/";
        }, 3000);
      },
      onError: (err: any) => {
        const errorMessage =
          err?.response?.data?.error || t("emailVerification.errorFallbackMessage");
        notification.error({
          message: t("emailVerification.errorNotificationMessage"),
          description: errorMessage,
          placement: "bottomRight",
        });
      },
    }
  );

  useEffect(() => {
    if (token && !verificationAttempted.current && !globalVerificationAttempted) {
      verificationAttempted.current = true;
      globalVerificationAttempted = true;
      mutate();
    }
  }, [token, mutate]);

  return (
    <AuthLayout maxWidth={540}>
      <VerificationContainer>
        {isLoading && (
          <>
            <Spin size="large" style={{ marginBottom: 16 }} />
            <StyledTitle level={2}>{t("emailVerification.loadingTitle")}</StyledTitle>
            <Message>{t("emailVerification.loadingMessage")}</Message>
          </>
        )}

        {isSuccess && (
          <>
            <IconWrapper success={true}>
              <CheckCircleOutlined />
            </IconWrapper>
            <StyledTitle level={2}>{t("emailVerification.successTitle")}</StyledTitle>
            <Message>
              {t("emailVerification.successMessageLine1")}
              <br />
              {t("emailVerification.successMessageLine2")}
            </Message>
          </>
        )}

        {isError && (
          <>
            <IconWrapper success={false}>
              <CloseCircleOutlined />
            </IconWrapper>
            <StyledTitle level={2}>{t("emailVerification.errorTitle")}</StyledTitle>
            <Message>
              {(error as any)?.response?.data?.error ||
                t("emailVerification.errorMessage")}
            </Message>
            <StyledButton onClick={() => window.location.href = "/register"}>
              {t("emailVerification.backToRegistration")}
            </StyledButton>
          </>
        )}
      </VerificationContainer>
    </AuthLayout>
  );
};
